/**
 * ClientRecorder Component
 * Records local participant video + audio in 2-second chunks for backup/redundancy
 * Chunks are stored in IndexedDB and continuously uploaded to S3
 */

import { useEffect, useRef } from 'react';
import { useLocalParticipant, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { saveChunk } from '../services/indexedDB';
import uploadManager from '../services/uploadManager';

const ClientRecorder = ({ meetingId, participantIdentity, isRecording, isPaused }) => {
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone], { onlySubscribed: false });

  const mediaRecorderRef = useRef(null);
  const segmentNumberRef = useRef(1);
  const chunkIndexRef = useRef(0);
  const isRecordingRef = useRef(false);
  const startAttemptCountRef = useRef(0);
  const wasPausedRef = useRef(false);

  // Handle recording state changes
  useEffect(() => {
    console.log('[ClientRecorder] Effect triggered:', {
      isRecording,
      isPaused,
      isRecordingRef: isRecordingRef.current,
      localParticipant: !!localParticipant,
      tracksCount: tracks.length
    });

    // Start recording if isRecording and not paused
    if (isRecording && !isPaused && !isRecordingRef.current && localParticipant && tracks.length > 0) {
      // Check if we're resuming from a pause
      if (wasPausedRef.current) {
        console.log('[ClientRecorder] Resuming client-side recording (new segment)');
        segmentNumberRef.current += 1;
        chunkIndexRef.current = 0;
        wasPausedRef.current = false;
      } else {
        console.log('[ClientRecorder] Starting client-side recording');
      }
      startClientRecording();
    }
    // Stop recording if paused
    else if (isPaused && isRecordingRef.current) {
      console.log('[ClientRecorder] Pausing client-side recording (segment complete)');
      wasPausedRef.current = true;
      stopClientRecording();
    }
    // Stop recording completely
    else if (!isRecording && isRecordingRef.current) {
      console.log('[ClientRecorder] Stopping client-side recording');
      stopClientRecording();
      segmentNumberRef.current = 1;
      chunkIndexRef.current = 0;
      wasPausedRef.current = false;
    }
  }, [isRecording, isPaused, localParticipant, tracks]);

  const startClientRecording = async () => {
    try {
      // Find audio and video tracks
      const audioTrack = tracks.find(t => t.source === Track.Source.Microphone);
      const videoTrack = tracks.find(t => t.source === Track.Source.Camera);

      // Wait for tracks to be fully initialized
      // Note: useTracks returns objects with structure { publication, source, participant }
      // The actual track is at track.publication.track.mediaStreamTrack
      if (!audioTrack?.publication?.track?.mediaStreamTrack && !videoTrack?.publication?.track?.mediaStreamTrack) {
        startAttemptCountRef.current += 1;
        console.warn(`[ClientRecorder] Tracks not yet initialized, attempt ${startAttemptCountRef.current}, retrying in 500ms...`);

        // Retry up to 10 times (5 seconds total)
        if (startAttemptCountRef.current < 10) {
          setTimeout(() => {
            startClientRecording();
          }, 500);
        } else {
          console.error('[ClientRecorder] Failed to initialize tracks after 10 attempts');
          startAttemptCountRef.current = 0;
        }
        return;
      }

      // Reset attempt counter on success
      startAttemptCountRef.current = 0;

      // Collect available media stream tracks
      const mediaStreamTracks = [];

      if (audioTrack?.publication?.track?.mediaStreamTrack) {
        mediaStreamTracks.push(audioTrack.publication.track.mediaStreamTrack);
        console.log('[ClientRecorder] Audio track found');
      }

      if (videoTrack?.publication?.track?.mediaStreamTrack) {
        mediaStreamTracks.push(videoTrack.publication.track.mediaStreamTrack);
        console.log('[ClientRecorder] Video track found');
      }

      if (mediaStreamTracks.length === 0) {
        console.warn('[ClientRecorder] No media stream tracks available yet');
        return;
      }

      const mediaStream = new MediaStream(mediaStreamTracks);
      const hasVideo = videoTrack?.publication?.track?.mediaStreamTrack != null;

      // Choose codec based on browser support and available tracks
      let mimeType;
      if (hasVideo) {
        // Try video codecs with audio
        if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
          mimeType = 'video/webm;codecs=vp9,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
          mimeType = 'video/webm;codecs=vp8,opus';
        } else if (MediaRecorder.isTypeSupported('video/webm')) {
          mimeType = 'video/webm';
        } else {
          mimeType = 'video/mp4';
        }
      } else {
        // Audio only fallback
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else {
          mimeType = 'audio/ogg';
        }
      }

      console.log('[ClientRecorder] Using MIME type:', mimeType, 'hasVideo:', hasVideo);

      const recorderOptions = {
        mimeType: mimeType,
        audioBitsPerSecond: 64000  // 64kbps for audio
      };

      // Add video bitrate if recording video
      if (hasVideo) {
        recorderOptions.videoBitsPerSecond = 1500000;  // 1.5 Mbps for video
      }

      const recorder = new MediaRecorder(mediaStream, recorderOptions);

      recorder.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0) {
          console.log(`[ClientRecorder] Chunk available: segment=${segmentNumberRef.current}, chunk=${chunkIndexRef.current}, size=${event.data.size} bytes`);

          const chunkData = {
            meeting_id: meetingId,
            participant_identity: participantIdentity,
            segment_number: segmentNumberRef.current,
            chunk_index: chunkIndexRef.current,
            blob: event.data,
            size_bytes: event.data.size,
            timestamp: Date.now(),
            upload_status: 'pending',
            retry_count: 0,
            last_retry_at: null,
            has_video: hasVideo
          };

          try {
            // Save to IndexedDB
            const chunkId = await saveChunk(chunkData);
            console.log(`[ClientRecorder] Chunk saved to IndexedDB: id=${chunkId}`);

            // Enqueue for upload
            uploadManager.enqueueChunk({ ...chunkData, id: chunkId });

            chunkIndexRef.current += 1;
          } catch (error) {
            console.error('[ClientRecorder] Error saving chunk:', error);

            // Check if quota exceeded
            if (error.name === 'QuotaExceededError') {
              console.error('[ClientRecorder] IndexedDB quota exceeded! Stopping recording.');
              stopClientRecording();
              // TODO: Notify user about quota issue
            }
          }
        }
      };

      recorder.onerror = (event) => {
        console.error('[ClientRecorder] MediaRecorder error:', event.error);
      };

      recorder.onstart = () => {
        console.log(`[ClientRecorder] MediaRecorder started: segment=${segmentNumberRef.current}`);
        isRecordingRef.current = true;
      };

      recorder.onstop = () => {
        console.log(`[ClientRecorder] MediaRecorder stopped: segment=${segmentNumberRef.current}`);
        isRecordingRef.current = false;
      };

      // Start recording with 2-second timeslice (generates chunks every 2 seconds)
      // This provides redundancy - if browser crashes, you only lose max 2 seconds
      recorder.start(2000);
      mediaRecorderRef.current = recorder;

    } catch (error) {
      console.error('[ClientRecorder] Error starting recording:', error);
    }
  };

  const stopClientRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();

        // Wait a bit for the final chunk to be processed
        setTimeout(() => {
          mediaRecorderRef.current = null;
        }, 100);
      } catch (error) {
        console.error('[ClientRecorder] Error stopping recording:', error);
        mediaRecorderRef.current = null;
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // This component doesn't render anything - it's purely functional
  return null;
};

export default ClientRecorder;
